import { BaseService } from './base-service';
import {
	GetKimLhcRequestDTO,
	GetKimLhcResponseDTO,
	CreateKimLhcRequestDTO,
	CreateKimLhcResponseDTO,
	UpdateKimLhcRequestDTO,
	UpdateKimLhcResponseDTO,
	CalculateScoresRequestDTO,
	CalculateScoresResponseDTO,
	GetKimLhcResultRequestDTO,
	GetKimLhcResultResponseDTO,
	CreateKimLhcConsolidationResponseDTO,
	CreateKimLhcConsolidationRequestDTO,
	CreateKimLhcPdfResponseDTO,
	CreateKimLhcPdfRequestDTO,
	RiskEvaluationRequestDTO,
	RiskEvaluationResponseDTO,
	GetVideoInfoRequestDTO,
	GetVideoInfoResponseDTO
} from '@/core/dto/kim-lhc';
import { GetKimLhcResultMapper } from '@/core/mapper/kim-lhc';
import type { KimLhcResult } from '@/types';

export class KimLhcService extends BaseService<GetKimLhcResponseDTO> {
	private static instance: KimLhcService;

	private constructor(public readonly basePath: string = '/kim-lhc') {
		super();
	}

	public static getInstance(): KimLhcService {
		if (!KimLhcService.instance) {
			KimLhcService.instance = new KimLhcService();
		}
		return KimLhcService.instance;
	}

	public async show(params: GetKimLhcRequestDTO): Promise<GetKimLhcResponseDTO> {
		const url = `${this.basePath}/${params.file_id}`;
		const { data } = await this.getInstance().get<GetKimLhcResponseDTO>(url, {
			params: {
				company_id: params.company_id,
				organization_id: params.organization_id
			}
		});
		return data;
	}

	public async create(params: CreateKimLhcRequestDTO): Promise<CreateKimLhcResponseDTO> {
		const url = this.basePath;
		const { data } = await this.getInstance().post<{
			data: CreateKimLhcResponseDTO;
		}>(url, params);

		return data.data;
	}

	public async update(params: UpdateKimLhcRequestDTO): Promise<UpdateKimLhcResponseDTO> {
		const { id, ...body } = params;
		const url = `${this.basePath}/${id}`;
		const { data } = await this.getInstance().patch<UpdateKimLhcResponseDTO>(url, body);
		return data;
	}

	public async calculateScores(params: CalculateScoresRequestDTO): Promise<CalculateScoresResponseDTO> {
		const url = `${this.basePath}/scores`;
		const { data } = await this.getInstance().post<{
			data: CalculateScoresResponseDTO;
		}>(url, params);
		return data.data;
	}

	public async getResult(params: GetKimLhcResultRequestDTO): Promise<KimLhcResult> {
		const url = `${this.basePath}/result/${params.id}`;
		const { data } = await this.getInstance().get<GetKimLhcResultResponseDTO>(url, {
			params: {
				company_id: params.company_id,
				organization_id: params.organization_id
			}
		});

		return new GetKimLhcResultMapper(data).toDomain();
	}

	public async downloadPDF(params: CreateKimLhcPdfRequestDTO): Promise<CreateKimLhcPdfResponseDTO> {
		const { id, ...body } = params;
		const url = `${this.basePath}/pdf/${id}`;
		const { data } = await this.getInstance().post<CreateKimLhcPdfResponseDTO>(url, body);
		return data;
	}

	public async consolidate(
		params: CreateKimLhcConsolidationRequestDTO
	): Promise<CreateKimLhcConsolidationResponseDTO> {
		const { id, ...body } = params;
		const url = `${this.basePath}/consolidate/${id}`;
		const { data } = await this.getInstance().post<CreateKimLhcConsolidationResponseDTO>(url, body);
		return data;
	}

	public async getRiskEvaluation(params: RiskEvaluationRequestDTO): Promise<RiskEvaluationResponseDTO> {
		const url = `${this.basePath}/risk-evaluation`;
		const { data } = await this.getInstance().get<RiskEvaluationResponseDTO>(url, { params });
		return data;
	}

	public async getVideoInfo(params: GetVideoInfoRequestDTO): Promise<GetVideoInfoResponseDTO> {
		const { organization_id, company_id, file_id } = params;
		const url = `${this.basePath}/video-info/${file_id}`;
		const { data } = await this.getInstance().get<GetVideoInfoResponseDTO>(url, {
			params: { organization_id, company_id }
		});
		return data;
	}
}
